import { produce } from 'immer';
import { useCallback, useEffect, useState } from 'react';

import { type IFormModalProps } from '@gbs-monorepo-packages/common';

import { type ICoursePage } from '../../../../../../../contexts/coursePage';
import { useCoursePages } from '../../../../../../../hooks/useCoursePages';
import { type IMatchTemplateColors } from '../../../../../../../services/templates';
import { ColorPickerSelectData } from '../../../../../../ColorPickerSelectData';
import { ColorPickerContainer, Container, FormModalCustom } from './styles';

interface IMatchSvgColorsModalProps
  extends Partial<
    Omit<IFormModalProps, 'children' | 'onAccept' | 'onOpenChange'>
  > {
  loading: boolean;
  colorsDefault: IMatchTemplateColors;
  onAccept: (page: ICoursePage) => Promise<void> | void;
  onDecline: () => void;
  open: boolean;
  waitToOpen?: boolean;
  pageId: number;
}

export const MatchSvgColorsModal = ({
  loading,
  colorsDefault,
  onAccept,
  onDecline,
  open,
  waitToOpen = false,
  pageId,
  ...props
}: IMatchSvgColorsModalProps): JSX.Element | null => {
  const { coursePages } = useCoursePages();
  const [templateColors, setTemplateColors] =
    useState<IMatchTemplateColors>(colorsDefault);

  useEffect(() => {
    const transformedObject: IMatchTemplateColors = {
      ...colorsDefault,
    };

    setTemplateColors(transformedObject);
  }, [colorsDefault]);

  const handleDecline = useCallback(() => {
    onDecline?.();
  }, [onDecline]);

  const handleAccept = useCallback(async () => {
    const coursePagesAux = coursePages;
    const updatedCoursePagesAux = coursePagesAux.map((page) => {
      if (page.id === pageId) {
        return {
          ...page,
          templateColorsFound: templateColors,
        };
      } else {
        return page;
      }
    });

    const pageFound = updatedCoursePagesAux.find((page) => page.id === pageId);
    if (pageFound) {
      await onAccept(pageFound);
    }
  }, [templateColors, onAccept, pageId, coursePages]);

  return !open ? null : (
    <FormModalCustom
      hiddenCloseButton
      acceptText="Save"
      dataCy="match-svg-colors-dialog-modal"
      mainText="Match Template Styles"
      disabled={false}
      {...props}
      onAccept={() => {
        void handleAccept();
      }}
      onDecline={handleDecline}
      loading={loading}
      open={!waitToOpen}
    >
      <Container>
        {Object.entries(templateColors).map(([colorKey, colorValue], index) => (
          <ColorPickerContainer key={index}>
            <ColorPickerSelectData
              color={String(colorValue)}
              dataCy={`color-picker-${colorKey}`}
              errorMessage=""
              label={`Import color`}
              onChange={(color, colorKey) => {
                if (colorKey) {
                  const newTemplateColors = produce(templateColors, (draft) => {
                    draft[colorKey] = color;
                  });
                  setTemplateColors(newTemplateColors);
                }
              }}
              colorKey={colorKey}
            />
          </ColorPickerContainer>
        ))}
      </Container>
    </FormModalCustom>
  );
};
